import styled from "styled-components";
import { motion } from "framer-motion";
import ParticleBackground from "../components/ParticleBackground";
import { 
  FaPython, 
  FaJava, 
  FaJsSquare, 
  FaHtml5, 
  FaCss3Alt, 
  FaReact, 
  FaNodeJs, 
  FaGitAlt, 
  FaGithub, 
  FaAndroid,
  FaDatabase
} from "react-icons/fa";
import { 
  SiC, 
  SiExpress, 
  SiFlask, 
  SiGooglechrome, 
  SiGooglecolab, 
  SiTensorflow, 
  SiScikitlearn, 
  SiOpencv, 
  SiMongodb, 
  SiMysql, 
  SiPandas, 
  SiNumpy
} from "react-icons/si";
import { MdBarChart, MdShowChart } from "react-icons/md";
import { VscCode } from "react-icons/vsc";

const SkillsContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px 20px;
  text-align: center;
  position: relative;
  z-index: 1;

  @media (max-width: 768px) {
    padding: 60px 20px 20px;
  }

  @media (max-width: 480px) {
    padding: 40px 10px 10px;
  }
`;

const Title = styled.h2`
  font-size: 2.5rem;
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: 40px;

  @media (max-width: 768px) {
    font-size: 2rem;
  }

  @media (max-width: 480px) {
    font-size: 1.8rem;
    margin-bottom: 30px;
  }
`;

const CategoryTitle = styled.h3`
  font-size: 2rem;
  color: ${({ theme }) => theme.colors.secondary};
  margin: 40px 0 20px;

  @media (max-width: 768px) {
    font-size: 1.8rem;
    margin: 30px 0 15px;
  }

  @media (max-width: 480px) {
    font-size: 1.5rem;
    margin: 20px 0 10px;
  }
`;

const SkillsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 20px;
  width: 100%;
  max-width: 1200px;
  padding: 0 20px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
    gap: 15px;
  }

  @media (max-width: 480px) {
    grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
    gap: 10px;
  }
`;

const SkillIcon = styled.div`
  font-size: 3rem;
  color: ${({ theme, color }) => color || theme.colors.primary};
  margin-bottom: 15px;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 80px;
  width: 80px;
  margin: 0 auto 15px;
  background: rgba(100, 255, 218, 0.1);
  border-radius: 50%;
  transition: all 0.3s ease;

  svg {
    width: 50px;
    height: 50px;
  }

  @media (max-width: 768px) {
    font-size: 2.5rem;
    height: 70px;
    width: 70px;
    
    svg {
      width: 40px;
      height: 40px;
    }
  }

  @media (max-width: 480px) {
    font-size: 2rem;
    height: 60px;
    width: 60px;
    
    svg {
      width: 35px;
      height: 35px;
    }
  }
`;

const SkillCard = styled(motion.div)`
  background: linear-gradient(135deg, rgba(10, 25, 47, 0.9), rgba(20, 35, 60, 0.8));
  padding: 25px 20px;
  border-radius: 15px;
  box-shadow: 0 8px 32px rgba(100, 255, 218, 0.2);
  border: 1px solid rgba(100, 255, 218, 0.3);
  text-align: center;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  position: relative;
  overflow: hidden;

  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 15px 45px rgba(100, 255, 218, 0.4);
    border-color: rgba(100, 255, 218, 0.6);
    
    ${SkillIcon} {
      background: rgba(100, 255, 218, 0.2);
      transform: scale(1.1);
    }
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 2px;
    background: linear-gradient(90deg, transparent, ${({ theme }) => theme.colors.primary}, transparent);
    transition: left 0.5s ease;
  }

  &:hover::before {
    left: 100%;
  }

  @media (max-width: 768px) {
    padding: 20px 15px;
  }

  @media (max-width: 480px) {
    padding: 15px 10px;
  }
`;

const SkillName = styled.h3`
  font-size: 1.2rem;
  color: ${({ theme }) => theme.colors.text};

  @media (max-width: 768px) {
    font-size: 1rem;
  }

  @media (max-width: 480px) {
    font-size: 0.9rem;
  }
`;

const skillsData = {
  Languages: [
    { name: "Python", icon: FaPython, color: "#3776ab" },
    { name: "C", icon: SiC, color: "#A8B9CC" },
    { name: "Java", icon: FaJava, color: "#ed8b00" },
    { name: "JavaScript", icon: FaJsSquare, color: "#f7df1e" },
    { name: "HTML", icon: FaHtml5, color: "#e34f26" },
    { name: "CSS", icon: FaCss3Alt, color: "#1572b6" },
  ],
  Frameworks: [
    { name: "React", icon: FaReact, color: "#61dafb" },
    { name: "Node.js", icon: FaNodeJs, color: "#68a063" },
    { name: "Express", icon: SiExpress, color: "#000000" },
    { name: "Flask", icon: SiFlask, color: "#000000" },
  ],
  Tools: [
    { name: "Git", icon: FaGitAlt, color: "#f05032" },
    { name: "VS Code", icon: VscCode, color: "#007acc" },
    { name: "Google Chrome", icon: SiGooglechrome, color: "#4285f4" },
    { name: "GitHub", icon: FaGithub, color: "#181717" },
    { name: "Google Colab", icon: SiGooglecolab, color: "#f9ab00" },
    { name: "Android Studio", icon: FaAndroid, color: "#3ddc84" },
  ],
  "AI/ML": [
    { name: "TensorFlow", icon: SiTensorflow, color: "#ff6f00" },
    { name: "Scikit-Learn", icon: SiScikitlearn, color: "#f7931e" },
    { name: "Computer Vision", icon: SiOpencv, color: "#5c3ee8" },
    { name: "Natural Language Processing", icon: "🗣️", color: "#64ffda" },
  ],
  Databases: [
    { name: "SQL", icon: FaDatabase, color: "#336791" },
    { name: "MongoDB", icon: SiMongodb, color: "#4db33d" },
    { name: "MySQL", icon: SiMysql, color: "#4479a1" },
  ],
  Libraries: [
    { name: "Pandas", icon: SiPandas, color: "#150458" },
    { name: "NumPy", icon: SiNumpy, color: "#013243" },
    { name: "Matplotlib", icon: MdBarChart, color: "#11557c" },
    { name: "Seaborn", icon: MdShowChart, color: "#4c72b0" },
    { name: "OpenCV", icon: SiOpencv, color: "#5c3ee8" },
  ],
};

function Skills() {
  return (
    <SkillsContainer>
      <ParticleBackground />
      <Title>My Skills</Title>

      {Object.entries(skillsData).map(([category, skills], index) => (
        <div key={category}>
          <CategoryTitle>{category}</CategoryTitle>
          <SkillsGrid>
            {skills.map((skill, skillIndex) => {
              const IconComponent = skill.icon;
              return (
                <SkillCard
                  key={skillIndex}
                  initial={{ opacity: 0, y: 50 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: skillIndex * 0.1 }}
                >
                  <SkillIcon color={skill.color}>
                    {typeof IconComponent === 'string' ? (
                      IconComponent
                    ) : (
                      <IconComponent />
                    )}
                  </SkillIcon>
                  <SkillName>{skill.name}</SkillName>
                </SkillCard>
              );
            })}
          </SkillsGrid>
        </div>
      ))}
    </SkillsContainer>
  );
}

export default Skills;